import { useEffect, useState, useRef } from "react";
import { Link } from "react-router-dom";
import Navbar from "../components/Navbar";
import Footer from "../components/Footer";
import { useSEO } from "../Utils/useSEO";
import { ScrollReveal, ScrollRevealItem } from "../components/ScrollReveal";
import HeroGlobe from "../components/Three/HeroGlobe";
import HeroInteractiveBg from "../components/Three/HeroInteractiveBg";
import SmartDelivery3D from "../components/Three/SmartDelivery3D";
import moduleSupplier from "../assets/images/module_supplier.png";
import moduleCollaboration from "../assets/images/module_collaboration.png";
import moduleProcurement from "../assets/images/module_procurement.png";
import moduleDelivery from "../assets/images/module_delivery.png";
import moduleAp from "../assets/images/module_ap.png";
import moduleAnalytics from "../assets/images/module_analytics.png";
import { ArrowRight, Globe, Cpu, Users2, CheckCircle2, ChevronRight, ShieldCheck, Eye, Power, } from "lucide-react";

const integrations = [
  {
    name: "SAP ERP",
    spec: "Bi-Directional",
    textColor: "text-brand-amber",
    defaultColor: "text-[#008FD3]",
    defaultGlow: "drop-shadow-[0_0_6px_rgba(0,143,211,0.25)]",
    hoverColor: "group-hover:text-[#008FD3]",
    hoverGlow: "group-hover:drop-shadow-[0_0_12px_rgba(0,143,211,0.5)]",
    svg: (
      <svg role="img" viewBox="0 0 24 24" className="w-8 h-8 fill-current">
        <title>SAP</title>
        <path d="M0 6.064v11.872h12.13L24 6.064zm3.264 2.208h.005c.863.001 1.915.245 2.676.633l-.82 1.43c-.835-.404-1.255-.442-1.73-.467-.708-.038-1.064.215-1.069.488-.007.332.669.633 1.305.838.964.306 2.19.715 2.377 1.9L7.77 8.437h2.046l2.064 5.576-.007-5.575h2.37c2.257 0 3.318.764 3.318 2.519 0 1.575-1.09 2.514-2.936 2.514h-.763l-.01 2.094-3.588-.003-.25-.908c-.37.122-.787.189-1.23.189-.456 0-.885-.071-1.263-.2l-.358.919-2 .006.09-.462c-.029.025-.057.05-.087.074-.535.43-1.208.629-2.037.644l-.213.002a5.075 5.075 0 0 1-2.581-.675l.73-1.448c.79.467 1.286.572 1.956.558.347-.007.598-.07.761-.239a.557.557 0 0 0 .156-.369c.007-.376-.53-.553-1.185-.756-.531-.164-1.135-.389-1.606-.735-.559-.41-.825-.924-.812-1.65a1.99 1.99 0 0 1 .566-1.377c.519-.537 1.357-.863 2.363-.863zm10.597 1.67v1.904h.521c.694 0 1.247-.23 1.248-.964 0-.709-.554-.94-1.248-.94zm-5.087.767l-.748 2.362c.223.085.481.133.757.133.268 0 .52-.047.742-.126l-.736-2.37z" />
      </svg>
    ),
  },
  {
    name: "ORACLE",
    spec: "Cloud Sync",
    textColor: "text-brand-orange",
    defaultColor: "text-[#F01F0A]",
    defaultGlow: "drop-shadow-[0_0_6px_rgba(240,31,10,0.25)]",
    hoverColor: "group-hover:text-[#F01F0A]",
    hoverGlow: "group-hover:drop-shadow-[0_0_12px_rgba(240,31,10,0.5)]",
    svg: (
      <svg role="img" viewBox="0 0 24 24" className="w-8 h-8 fill-current">
        <title>Oracle</title>
        <path d="M16.412 4.412h-8.82a7.588 7.588 0 0 0-.008 15.176h8.828a7.588 7.588 0 0 0 0-15.176zm-.193 12.502H7.786a4.915 4.915 0 0 1 0-9.828h8.433a4.914 4.914 0 1 1 0 9.828z" />
      </svg>
    ),
  },
  {
    name: "MICROSOFT",
    spec: "Dynamics 365",
    textColor: "text-brand-gold",
    defaultColor: "text-white",
    defaultGlow: "drop-shadow-[0_0_6px_rgba(255,255,255,0.15)]",
    hoverColor: "group-hover:text-white",
    hoverGlow: "group-hover:drop-shadow-[0_0_12px_rgba(255,255,255,0.3)]",
    svg: (
      <svg role="img" viewBox="0 0 24 24" className="w-8 h-8">
        <title>Microsoft</title>
        <g>
          <rect x="0" y="0" width="11.4" height="11.4" className="fill-current text-[#F25022] transition-colors duration-300" />
          <rect x="12.6" y="0" width="11.4" height="11.4" className="fill-current text-[#7FBA00] transition-colors duration-300" />
          <rect x="0" y="12.6" width="11.4" height="11.4" className="fill-current text-[#00A4EF] transition-colors duration-300" />
          <rect x="12.6" y="12.6" width="11.4" height="11.4" className="fill-current text-[#FFB900] transition-colors duration-300" />
        </g>
      </svg>
    ),
  },
  {
    name: "REST API",
    spec: "Webhooks",
    textColor: "text-brand-amber",
    defaultColor: "text-[#00F2FE]",
    defaultGlow: "drop-shadow-[0_0_6px_rgba(0,242,254,0.25)]",
    hoverColor: "group-hover:text-[#00F2FE]",
    hoverGlow: "group-hover:drop-shadow-[0_0_12px_rgba(0,242,254,0.5)]",
    svg: (
      <svg viewBox="0 0 24 24" className="w-8 h-8 fill-current">
        <title>REST API</title>
        <path d="M12 1a4 4 0 0 0-4 4 4 4 0 0 0 .5 1.9L4.9 10.5A4 4 0 0 0 4 10a4 4 0 0 0-4 4 4 4 0 0 0 4 4 4 4 0 0 0 3.1-1.5l3.8 2.4A4 4 0 0 0 11 20a4 4 0 0 0 4 4 4 4 0 0 0 4-4 4 4 0 0 0-.1-.9l3.8-2.4A4 4 0 0 0 20 18a4 4 0 0 0 4-4 4 4 0 0 0-4-4 4 4 0 0 0-.9.1l-3.6-3.6A4 4 0 0 0 16 5a4 4 0 0 0-4-4zm0 2.5A1.5 1.5 0 1 1 12 5a1.5 1.5 0 0 1 0-1.5zm-8 10A1.5 1.5 0 1 1 4 15a1.5 1.5 0 0 1 0-1.5zm16 0A1.5 1.5 0 1 1 20 15a1.5 1.5 0 0 1 0-1.5zm-8 6A1.5 1.5 0 1 1 12 20a1.5 1.5 0 0 1 0-1.5zM12 7.5a4.5 4.5 0 0 0-.8.1L9 11.2a4.5 4.5 0 0 0 .5 2.1l-.8 1.3a4.5 4.5 0 0 0-.7-.1c.4.6.9 1.1 1.5 1.4.3-.4.7-.7 1.2-.8l.8-2.6a4.5 4.5 0 0 0-.5-2.1l2.2-2.2a4.5 4.5 0 0 0 .8.1z" />
      </svg>
    ),
  },
  {
    name: "SWIFT",
    spec: "Bank Ledger",
    textColor: "text-brand-gold",
    defaultColor: "text-[#FA7343]",
    defaultGlow: "drop-shadow-[0_0_6px_rgba(250,115,67,0.25)]",
    hoverColor: "group-hover:text-[#FA7343]",
    hoverGlow: "group-hover:drop-shadow-[0_0_12px_rgba(250,115,67,0.5)]",
    svg: (
      <svg role="img" viewBox="0 0 24 24" className="w-8 h-8 fill-current">
        <title>Swift</title>
        <path d="M7.508 0c-.287 0-.573 0-.86.002-.241.002-.483.003-.724.01-.132.003-.263.009-.395.015A9.154 9.154 0 0 0 4.348.15 5.492 5.492 0 0 0 2.85.645 5.04 5.04 0 0 0 .645 2.848c-.245.48-.4.972-.495 1.5-.093.52-.122 1.05-.136 1.576a35.2 35.2 0 0 0-.012.724C0 6.935 0 7.221 0 7.508v8.984c0 .287 0 .575.002.862.002.24.005.481.012.722.014.526.043 1.057.136 1.576.095.528.25 1.02.495 1.5a5.03 5.03 0 0 0 2.205 2.203c.48.244.97.4 1.498.495.52.093 1.05.124 1.576.138.241.007.483.009.724.01.287.002.573.002.86.002h8.984c.287 0 .573 0 .86-.002.241-.001.483-.003.724-.01a10.523 10.523 0 0 0 1.578-.138 5.322 5.322 0 0 0 1.498-.495 5.035 5.035 0 0 0 2.203-2.203c.245-.48.4-.972.495-1.5.093-.52.124-1.05.138-1.576.007-.241.009-.481.01-.722.002-.287.002-.575.002-.862V7.508c0-.287 0-.573-.002-.86a33.662 33.662 0 0 0-.01-.724 10.5 10.5 0 0 0-.138-1.576 5.328 5.328 0 0 0-.495-1.5A5.039 5.039 0 0 0 21.152.645 5.32 5.32 0 0 0 19.654.15a10.493 10.493 0 0 0-1.578-.138 34.98 34.98 0 0 0-.722-.01C17.067 0 16.779 0 16.492 0H7.508zm6.035 3.41c4.114 2.47 6.545 7.162 5.549 11.131-.024.093-.05.181-.076.272l.002.001c2.062 2.538 1.5 5.258 1.236 4.745-1.072-2.086-3.066-1.568-4.088-1.043a6.803 6.803 0 0 1-.281.158l-.02.012-.002.002c-2.115 1.123-4.957 1.205-7.812-.022a12.568 12.568 0 0 1-5.64-4.838c.649.48 1.35.902 2.097 1.252 3.019 1.414 6.051 1.311 8.197-.002C9.651 12.73 7.101 9.67 5.146 7.191a10.628 10.628 0 0 1-1.005-1.384c2.34 2.142 6.038 4.83 7.365 5.576C8.69 8.408 6.208 4.743 6.324 4.86c4.436 4.47 8.528 6.996 8.528 6.996.154.085.27.154.36.213.085-.215.16-.437.224-.668.708-2.588-.09-5.548-1.893-7.992z" />
      </svg>
    ),
  },
  {
    name: "DOCUSIGN",
    spec: "E-Signature",
    textColor: "text-brand-orange",
    defaultColor: "text-[#0057B8]",
    defaultGlow: "drop-shadow-[0_0_6px_rgba(0,87,184,0.25)]",
    hoverColor: "group-hover:text-[#0057B8]",
    hoverGlow: "group-hover:drop-shadow-[0_0_12px_rgba(0,87,184,0.5)]",
    svg: (
      <svg role="img" viewBox="0 0 24 24" className="w-8 h-8 fill-current">
        <title>DocuSign</title>
        <path d="M9.517 3.31h4.966v6.621h3.31L12 16.552 6.207 9.931h3.31V3.31zM0 19.034h24v1.655H0v-1.655z" />
      </svg>
    ),
  },
  {
    name: "SALESFORCE",
    spec: "CRM Sync",
    textColor: "text-brand-orange",
    defaultColor: "text-[#00A1E0]",
    defaultGlow: "drop-shadow-[0_0_6px_rgba(0,161,224,0.25)]",
    hoverColor: "group-hover:text-[#00A1E0]",
    hoverGlow: "group-hover:drop-shadow-[0_0_12px_rgba(0,161,224,0.5)]",
    svg: (
      <svg role="img" viewBox="0 0 24 24" className="w-8 h-8 fill-current">
        <title>Salesforce</title>
        <path d="M18.29 11.23a4.26 4.26 0 00-6.72-3.8 5.75 5.75 0 00-10.4 2.8 4.2 4.2 0 00.32 8.36h16.8a4.2 4.2 0 000-8.36zm-8.2 4.93a1.53 1.53 0 01-1.53-1.53 1.53 1.53 0 011.53-1.53 1.53 0 011.53 1.53 1.53 0 01-1.53 1.53z" />
      </svg>
    ),
  },
];

const modules = [
  {
    title: "1. Supplier Management",
    desc: "Self-service registration portal with verification of tax profiles, ISO certificates, and background status checks.",
    image: moduleSupplier,
    colorClass: "text-brand-amber",
    hoverBorder: "hover:border-brand-amber/40",
    hoverColor: "group-hover:text-brand-amber",
  },
  {
    title: "2. Vendor Collaboration",
    desc: "Consolidated workspace containing dynamic SLA metrics, contract renewal timelines, and secure messages.",
    image: moduleCollaboration,
    colorClass: "text-brand-gold",
    hoverBorder: "hover:border-brand-gold/40",
    hoverColor: "group-hover:text-brand-gold",
  },
  {
    title: "3. Procurement Automation",
    desc: "Smart contracts monitor warehouse inventory thresholds to automatically trigger RFQs, dispatch POs, and verify compliance.",
    image: moduleProcurement,
    colorClass: "text-brand-orange",
    hoverBorder: "hover:border-brand-orange/40",
    hoverColor: "group-hover:text-brand-orange",
  },
  {
    title: "4. Smart Delivery",
    desc: "Coordinates incoming logistics with JIT warehouse dock schedules, check-in gates, and real-time transit telemetry.",
    image: moduleDelivery,
    colorClass: "text-brand-orange",
    hoverBorder: "hover:border-brand-orange/40",
    hoverColor: "group-hover:text-brand-orange",
  },
  {
    title: "5. AP Automation",
    desc: "3-way mismatch audits verify PO receipts against invoices using secure AI matching before dispatching bank payouts.",
    image: moduleAp,
    colorClass: "text-brand-gold",
    hoverBorder: "hover:border-brand-gold/40",
    hoverColor: "group-hover:text-brand-gold",
  },
  {
    title: "6. Analytics & Reporting",
    desc: "Generate dynamic graphs outlining consolidated spend data by category, predictive delivery rates, and threat grading.",
    image: moduleAnalytics,
    colorClass: "text-brand-amber",
    hoverBorder: "hover:border-brand-amber/40",
    hoverColor: "group-hover:text-brand-amber",
  },
];

const clients = [
  {
    name: "KALS GROUP",
    industry: "Retail & Distribution",
    useCase: "Distributor Portal & JIT Logistics",
    metric: "98.7% Order Fulfillment",
    borderColor: "hover:border-[#ef4444]/30",
    svg: (
      <svg className="w-32 h-8" viewBox="0 0 160 36">
        <defs>
          <radialGradient id="kalsGrad" cx="50%" cy="50%" r="50%">
            <stop offset="0%" stopColor="#ef4444" />
            <stop offset="100%" stopColor="#7f1d1d" />
          </radialGradient>
        </defs>
        <circle cx="18" cy="18" r="14" fill="url(#kalsGrad)" stroke="#ff4444" strokeWidth="1" />
        <path d="M11 13 C11 9, 14 9, 18 11 C22 9, 25 9, 25 13 C25 21, 21 24, 18 24 C15 24, 11 21, 11 13 Z" fill="black" />
        <circle cx="15.5" cy="17" r="2.5" fill="white" />
        <circle cx="15.5" cy="17" r="1" fill="red" />
        <circle cx="20.5" cy="17" r="2.5" fill="white" />
        <circle cx="20.5" cy="17" r="1" fill="red" />
        <polygon points="18,17 17,21 19,21" fill="#ff4444" />
        <path d="M11 13 L13 10 L15 13 M21 13 L23 10 L25 13" stroke="black" strokeWidth="1.5" fill="none" />
        <text x="40" y="24" fill="currentColor" className="text-[11px] font-bold tracking-widest font-sans">KALS GROUP</text>
      </svg>
    )
  },
  {
    name: "MCLAREN",
    industry: "High-Performance Automotive",
    useCase: "Parts Supply Traceability",
    metric: "12ms Supplier Sync",
    borderColor: "hover:border-[#ff3e00]/30",
    svg: (
      <svg className="w-28 h-8" viewBox="0 0 140 36">
        <text x="0" y="24" fill="currentColor" className="text-lg font-black italic tracking-tighter">McLaren</text>
        <path d="M80 8 C84 6, 92 7, 95 12 C92 9, 85 10, 81 13 C78 15, 75 17, 72 20 C74 16, 76 11, 80 8 Z" fill="#ff3e00" />
      </svg>
    )
  },
  {
    name: "QUESS",
    industry: "Workforce & Operations",
    useCase: "Contractor Compliance Control",
    metric: "100% Payroll Accuracy",
    borderColor: "hover:border-[#00b4d8]/30",
    svg: (
      <svg className="w-28 h-8" viewBox="0 0 130 36">
        <text x="0" y="22" className="text-xl font-black tracking-tight" fill="#00b4d8">Q</text>
        <text x="16" y="22" className="text-xl font-black tracking-tight" fill="currentColor">UESS</text>
        <text x="2" y="32" className="text-[6px] font-bold tracking-[0.25em]" fill="currentColor" opacity="0.8">WINNING TOGETHER</text>
      </svg>
    )
  },
  {
    name: "BRIGADE",
    industry: "Real Estate & Construction",
    useCase: "Material Logistics Control",
    metric: "4.2 Days Onboarding",
    borderColor: "hover:border-[#00b4d8]/30",
    svg: (
      <svg className="w-28 h-8" viewBox="0 0 130 36">
        <g fill="#00b4d8" opacity="0.9">
          <rect x="0" y="10" width="16" height="1.8" />
          <rect x="0" y="13.5" width="16" height="1.8" />
          <rect x="0" y="17" width="16" height="1.8" />
          <rect x="0" y="20.5" width="16" height="1.8" />
          <rect x="0" y="24" width="16" height="1.8" />
          <rect x="0" y="27.5" width="16" height="1.8" />
        </g>
        <circle cx="18" cy="6" r="3" fill="#fbbf24" />
        <text x="26" y="25" className="text-base font-bold tracking-wider" fill="currentColor">BRIGADE</text>
      </svg>
    )
  },
  {
    name: "PAYTM",
    industry: "FinTech & Payments",
    useCase: "AP Escrow Payment Automations",
    metric: "3.2M Daily Transactions",
    borderColor: "hover:border-[#3b82f6]/30",
    svg: (
      <svg className="w-24 h-8" viewBox="0 0 120 36">
        <text x="0" y="24" className="text-xl font-black tracking-tight" fill="#3b82f6">pay</text>
        <text x="32" y="24" className="text-xl font-semibold tracking-tight" fill="#00baf2">tm</text>
      </svg>
    )
  }
];

export default function HomePage() {
  useSEO({
    title: "Home // Next-Gen Supply Chain Platform",
    description: "Welcome to Supply Spot - the autonomous logistics command center and procurement matching suite for modern supply chain networks."
  });

  const [transactionText, setTransactionText] = useState("");
  const [transactionSteps, setTransactionSteps] = useState([]);
  const [isProcessingTx, setIsProcessingTx] = useState(false);
  const [txResult, setTxResult] = useState(null);

  // States and refs for integrations circular arch rotation
  const [rotationPhase, setRotationPhase] = useState(0);
  const [isPaused, setIsPaused] = useState(false);
  const isPausedRef = useRef(false);

  useEffect(() => {
    isPausedRef.current = isPaused;
  }, [isPaused]);

  useEffect(() => {
    let animationFrameId;
    let lastTime = performance.now();
    const speed = 8; // degrees per second

    const updateRotation = (time) => {
      const delta = (time - lastTime) / 1000;
      lastTime = time;
      if (!isPausedRef.current) {
        setRotationPhase((prev) => (prev + speed * delta) % 360);
      }
      animationFrameId = requestAnimationFrame(updateRotation);
    };

    animationFrameId = requestAnimationFrame(updateRotation);
    return () => cancelAnimationFrame(animationFrameId);
  }, []);


  const handleProcessTransaction = (text) => {
    if (!text.trim() || isProcessingTx) return;

    setIsProcessingTx(true);
    setTransactionSteps([]);
    setTxResult(null);

    // Identify vendor in text
    let matchedVendor = "Unknown Vendor";
    let amount = "$0.00";
    let status = "FLAGGED (Needs Audit)";

    const lowerText = text.toLowerCase();
    if (lowerText.includes("mclaren")) {
      matchedVendor = "McLaren";
      amount = "$180,000.00";
      status = "COMPLIANT";
    } else if (lowerText.includes("brigade")) {
      matchedVendor = "BRIGADE";
      amount = "$45,000.00";
      status = "COMPLIANT";
    } else if (lowerText.includes("kals")) {
      matchedVendor = "KALS GROUP";
      amount = "$25,000.00";
      status = "COMPLIANT";
    } else if (lowerText.includes("quess")) {
      matchedVendor = "QUESS";
      amount = "$12,500.00";
      status = "COMPLIANT";
    } else if (lowerText.includes("paytm")) {
      matchedVendor = "Paytm";
      amount = "$8,400.00";
      status = "COMPLIANT";
    }

    const steps = [
      "Initializing Aether Secure Terminal...",
      `Parsing text directive: "${text.substring(0, 40)}..."`,
      `Vendor Identified: ${matchedVendor.toUpperCase()}`,
      "Checking compliance registries and active ISO certification...",
      "Verifying 3-way match: PO matching invoice metadata...",
      "Generating secure AP ledger ledger records...",
      "Broadcasting hash to supply chain nodes..."
    ];

    let currentStep = 0;
    const interval = setInterval(() => {
      if (currentStep < steps.length) {
        setTransactionSteps(prev => [...prev, steps[currentStep]]);
        currentStep++;
      } else {
        clearInterval(interval);
        setIsProcessingTx(false);
        setTxResult({
          status: status,
          vendor: matchedVendor,
          amount: amount,
          hash: `0x${Math.random().toString(16).substring(2, 10).toUpperCase()}${Math.random().toString(16).substring(2, 10).toUpperCase()}`,
          timestamp: new Date().toLocaleTimeString()
        });
      }
    }, 450);
  };

  // Counter states for benefits section
  const [stats, setStats] = useState({
    onboarding: 0,
    processing: 0,
    accuracy: 0,
    visibility: 0,
    reduction: 0,
  });

  const statsSectionRef = useRef(null);

  useEffect(() => {
    // Scroll event listener for counting up stats when visible
    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting) {
          // Trigger animations
          const interval = setInterval(() => {
            setStats((prev) => {
              const nextOnboarding = prev.onboarding < 50 ? prev.onboarding + 1 : 50;
              const nextProcessing = prev.processing < 80 ? prev.processing + 2 : 80;
              const nextAccuracy = prev.accuracy < 95 ? prev.accuracy + 2 : 95;
              const nextVisibility = prev.visibility < 100 ? prev.visibility + 2 : 100;
              const nextReduction = prev.reduction < 30 ? prev.reduction + 1 : 30;

              if (
                nextOnboarding === 50 &&
                nextProcessing === 80 &&
                nextAccuracy === 95 &&
                nextVisibility === 100 &&
                nextReduction === 30
              ) {
                clearInterval(interval);
              }
              return {
                onboarding: nextOnboarding,
                processing: nextProcessing,
                accuracy: nextAccuracy,
                visibility: nextVisibility,
                reduction: nextReduction,
              };
            });
          }, 30);
          observer.disconnect();
        }
      },
      { threshold: 0.1 }
    );

    if (statsSectionRef.current) {
      observer.observe(statsSectionRef.current);
    }
    return () => observer.disconnect();
  }, []);

  return (
    <div className="min-h-screen bg-bg-primary text-text-primary overflow-x-hidden font-sans relative">
      {/* Page-wide Shimmer & Gradient Flow Background */}
      <div className="fixed inset-0 z-0 pointer-events-none">
        <div className="gradient-flow-bg opacity-20 absolute inset-0" />
      </div>

      <Navbar />

      {/* HERO SECTION */}
      <section id="hero" className="relative min-h-screen w-full flex items-center pt-24 pb-16 z-10 overflow-hidden">
        {/* Shimmer & Gradient Flow Background */}
        <div className="absolute inset-0 z-0 pointer-events-none">
          <div className="gradient-flow-bg opacity-35 absolute inset-0" />
        </div>

        {/* Interactive 3D Background Animation */}
        <HeroInteractiveBg />

        <div className="max-w-7xl mx-auto w-full px-6 grid grid-cols-1 lg:grid-cols-12 gap-12 items-center relative z-10">

          {/* Left Content */}
          <div className="lg:col-span-7 text-left flex flex-col gap-6 relative z-10">

            {/* Badge */}
            <div className="inline-flex items-center gap-2 self-start px-3 py-1.5 rounded-full border border-neon-cyan/20 bg-brand-amber/5 text-brand-amber text-xs font-title tracking-wider animate-pulse">
              <Globe className="w-3.5 h-3.5" />
              <span>SMART DELIVERY + JIT LOGISTICS PLATFORM</span>
            </div>

            {/* Headline */}
            <h1 className="text-4xl sm:text-5xl md:text-[75px] font-title font-bold tracking-tight leading-none">
              Next-Generation <br />
              <span className="text-transparent bg-clip-text bg-gradient-to-r from-brand-orange via-brand-amber to-brand-gold">
                Supplier
              </span>{" "}<br />
              Management
            </h1>

            <p className="text-text-secondary text-base md:text-lg leading-relaxed max-w-xl">
              Streamline supplier onboarding, procurement, logistics, accounts payable automation, and supplier performance management through one intelligent platform.
            </p>

            {/* Buttons */}
            <div className="flex flex-wrap items-center gap-4 mt-2">
              <Link
                to="/contact"
                className="px-6 py-3.5 font-semibold text-xs tracking-wider text-white bg-gradient-to-r from-brand-orange to-brand-amber rounded-lg shadow-md shadow-brand-orange/15 hover:shadow-brand-amber/30 hover:scale-[1.02] active:scale-[0.98] transition-all"
              >
                REQUEST DEMO
              </Link>
              <Link
                to="/features"
                className="px-6 py-3.5 font-semibold text-xs tracking-wider text-text-primary border border-text-muted/20 hover:border-brand-orange rounded-lg bg-transparent hover:text-brand-amber transition-all"
              >
                EXPLORE FEATURES
              </Link>
            </div>

            {/* Trust Indicators */}
            <div className="grid grid-cols-3 gap-4 border-t border-text-muted/10 pt-8 mt-4">
              <div>
                <div className="text-2xl font-title font-bold text-brand-amber">500+</div>
                <div className="text-[10px] font-bold text-text-muted uppercase tracking-wider">
                  Suppliers Connected
                </div>
              </div>
              <div>
                <div className="text-2xl font-title font-bold text-brand-orange">99.9%</div>
                <div className="text-[10px] font-bold text-text-muted uppercase tracking-wider">
                  Platform Uptime
                </div>
              </div>
              <div>
                <div className="text-2xl font-title font-bold text-brand-gold">REAL-TIME</div>
                <div className="text-[10px] font-bold text-text-muted uppercase tracking-wider">
                  Global Visibility
                </div>
              </div>
            </div>

          </div>

          {/* Right Content (3D Scene Wrapper) */}
          <div className="lg:col-span-5 h-[400px] sm:h-[500px] lg:h-[600px] w-full relative z-0 overflow-visible">
            {/* Background glow effects for sunset atmosphere */}
            <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[140%] h-[140%] bg-[radial-gradient(circle_at_center,rgba(249,115,22,0.12)_0%,rgba(245,158,11,0.06)_30%,rgba(0,0,0,0)_70%)] pointer-events-none z-0 filter blur-xl" />
            <div className="absolute -right-20 top-1/2 -translate-y-1/2 w-[350px] h-[350px] bg-brand-orange/10 rounded-full filter blur-[120px] opacity-45 pointer-events-none mix-blend-screen" />
            <div className="absolute -left-20 bottom-0 w-[300px] h-[300px] bg-neon-cyan/5 rounded-full filter blur-[100px] opacity-35 pointer-events-none mix-blend-screen" />

            <HeroGlobe />
          </div>

        </div>
      </section>

      {/* TRUSTED BY SECTION */}
      <section className="py-12 border-y border-text-muted/5 bg-bg-secondary/20 overflow-hidden relative z-10 select-none">
        <div className="w-full flex">
          <div className="flex gap-16 items-center whitespace-nowrap animate-marquee">
            {/* Repeated twice to handle continuous loop */}
            {[...Array(3)].map((_, loopIdx) => (
              <div key={loopIdx} className="flex gap-16 items-center">
                <span className="font-title text-sm tracking-widest text-text-muted/40 font-bold hover:text-brand-orange transition-colors">
                  MANUFACTURING CORE
                </span>
                <span className="font-title text-sm tracking-widest text-text-muted/40 font-bold hover:text-brand-amber transition-colors">
                  AUTOMOTIVE SYSTEMS
                </span>
                <span className="font-title text-sm tracking-widest text-text-muted/40 font-bold hover:text-brand-gold transition-colors">
                  RETAIL LOGISTICS
                </span>
                <span className="font-title text-sm tracking-widest text-text-muted/40 font-bold hover:text-brand-amber transition-colors">
                  ELECTRONICS HUB
                </span>
                <span className="font-title text-sm tracking-widest text-text-muted/40 font-bold hover:text-brand-orange transition-colors">
                  FMCG SUPPLY CHAIN
                </span>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ENTERPRISE CLIENTS SECTION */}
      <section className="py-24 max-w-7xl mx-auto px-6 relative z-10">
        <ScrollReveal className="max-w-3xl mx-auto mb-16 text-center">
          <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full border border-brand-orange/20 bg-brand-orange/5 text-brand-orange text-xs font-title tracking-wider mb-6">
            TRUSTED BY THE BEST
          </div>
          <h2 className="text-3xl sm:text-4xl font-title font-bold mb-6 tracking-wide">
            Enterprise Leaders Build on Supply Spot
          </h2>
          <p className="text-text-secondary text-base">
            From heavy manufacturing to high-velocity logistics, our command platform coordinates secure, automated workflows for global networks.
          </p>
        </ScrollReveal>

        <ScrollReveal staggerChildren={0.08} className="flex flex-wrap justify-center gap-8">
          {clients.map((client, index) => (
            <ScrollRevealItem
              key={index}
              className="w-full md:max-w-[calc(50%-1rem)] lg:max-w-[calc(33.33%-1.34rem)] flex-grow flex"
            >
              <div
                className={`glass-panel p-8 rounded-2xl border border-brand-orange/10 ${client.borderColor} transition-all duration-300 group flex flex-col justify-between gap-6 relative overflow-hidden w-full`}
              >
                {/* Corner tech accent line */}
                <div className="absolute top-0 right-0 w-8 h-8 border-t-2 border-r-2 border-transparent group-hover:border-brand-orange/30 transition-all duration-300" />

                <div className="flex justify-between items-start">
                  <div className="text-text-primary/75 group-hover:text-text-primary transition-colors duration-300">
                    {client.svg}
                  </div>
                  <span className="text-[10px] font-title font-bold uppercase tracking-widest text-text-muted/60 bg-bg-secondary/60 border border-text-muted/10 px-2.5 py-1 rounded">
                    {client.industry}
                  </span>
                </div>

                <div className="flex flex-col gap-2">
                  <div className="text-[10px] font-bold text-text-muted uppercase tracking-wider">
                    Operational Focus
                  </div>
                  <h4 className="text-lg font-title font-bold text-text-primary group-hover:text-brand-orange transition-colors duration-300 uppercase tracking-wide">
                    {client.useCase}
                  </h4>
                </div>

                <div className="border-t border-text-muted/10 pt-4 flex justify-between items-center">
                  <span className="text-[10px] font-bold text-text-muted uppercase tracking-wider">
                    Verified Metric
                  </span>
                  <span className="text-xs font-title font-bold text-transparent bg-clip-text bg-gradient-to-r from-brand-orange to-brand-amber">
                    {client.metric}
                  </span>
                </div>
              </div>
            </ScrollRevealItem>
          ))}
        </ScrollReveal>
      </section>

      {/* PRODUCT OVERVIEW */}
      <section id="overview" className="py-24 max-w-7xl mx-auto px-6 text-center relative z-10">
        <ScrollReveal className="max-w-3xl mx-auto mb-16 text-center">
          <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full border border-brand-orange/20 bg-brand-orange/5 text-brand-orange text-xs font-title tracking-wider mb-6">
            COMMAND CENTER CORE
          </div>
          <h2 className="text-3xl sm:text-4xl font-title font-bold mb-6 tracking-wide">
            One Platform. Complete Supply Chain Control.
          </h2>
          <p className="text-text-secondary text-base">
            A futuristic control dashboard combining verified onboarding profiles, automated RFQs, GPS cargo tracking, and 3-way matching.
          </p>
        </ScrollReveal>

        <ScrollReveal staggerChildren={0.1} className="grid grid-cols-1 md:grid-cols-3 gap-8">
          {/* Feature Block 1 */}
          <ScrollRevealItem className="flex">
            <div className="glass-panel p-8 rounded-2xl border border-brand-orange/10 hover:border-brand-amber/30 hover:-translate-y-1 transition-all duration-300 text-left flex flex-col gap-4 w-full">
              <div className="w-12 h-12 rounded-xl bg-neon-cyan/10 border border-neon-cyan flex items-center justify-center text-brand-amber shadow-md shadow-neon-cyan/15">
                <Users2 className="w-6 h-6" />
              </div>
              <h3 className="font-title text-lg font-bold">Supplier Collaboration</h3>
              <ul className="text-sm text-text-secondary flex flex-col gap-2.5 mt-2">
                <li className="flex items-center gap-2">
                  <CheckCircle2 className="w-4 h-4 text-brand-amber" />
                  <span>Self-Service Onboarding</span>
                </li>
                <li className="flex items-center gap-2">
                  <CheckCircle2 className="w-4 h-4 text-brand-amber" />
                  <span>Compliance Management</span>
                </li>
                <li className="flex items-center gap-2">
                  <CheckCircle2 className="w-4 h-4 text-brand-amber" />
                  <span>RFQ Collaboration</span>
                </li>
              </ul>
            </div>
          </ScrollRevealItem>

          {/* Feature Block 2 */}
          <ScrollRevealItem className="flex">
            <div className="glass-panel p-8 rounded-2xl border border-brand-orange/10 hover:border-brand-orange/30 hover:-translate-y-1 transition-all duration-300 text-left flex flex-col gap-4 w-full">
              <div className="w-12 h-12 rounded-xl bg-brand-orange/10 border border-brand-orange flex items-center justify-center text-brand-orange shadow-md shadow-brand-orange/15">
                <Globe className="w-6 h-6" />
              </div>
              <h3 className="font-title text-lg font-bold">Smart Delivery & JIT</h3>
              <ul className="text-sm text-text-secondary flex flex-col gap-2.5 mt-2">
                <li className="flex items-center gap-2">
                  <CheckCircle2 className="w-4 h-4 text-brand-orange" />
                  <span>Dynamic Scheduling</span>
                </li>
                <li className="flex items-center gap-2">
                  <CheckCircle2 className="w-4 h-4 text-brand-orange" />
                  <span>GPS Vehicle Tracking</span>
                </li>
                <li className="flex items-center gap-2">
                  <CheckCircle2 className="w-4 h-4 text-brand-orange" />
                  <span>Inventory Optimization</span>
                </li>
              </ul>
            </div>
          </ScrollRevealItem>

          {/* Feature Block 3 */}
          <ScrollRevealItem className="flex">
            <div className="glass-panel p-8 rounded-2xl border border-brand-orange/10 hover:border-brand-gold/30 hover:-translate-y-1 transition-all duration-300 text-left flex flex-col gap-4 w-full">
              <div className="w-12 h-12 rounded-xl bg-brand-gold/10 border border-neon-purple flex items-center justify-center text-brand-gold shadow-md shadow-neon-purple/15">
                <Cpu className="w-6 h-6" />
              </div>
              <h3 className="font-title text-lg font-bold">AP Automation</h3>
              <ul className="text-sm text-text-secondary flex flex-col gap-2.5 mt-2">
                <li className="flex items-center gap-2">
                  <CheckCircle2 className="w-4 h-4 text-brand-gold" />
                  <span>AI Invoice Matching</span>
                </li>
                <li className="flex items-center gap-2">
                  <CheckCircle2 className="w-4 h-4 text-brand-gold" />
                  <span>Automated Approval Flow</span>
                </li>
                <li className="flex items-center gap-2">
                  <CheckCircle2 className="w-4 h-4 text-brand-gold" />
                  <span>Escrow Payment Tracking</span>
                </li>
              </ul>
            </div>
          </ScrollRevealItem>
        </ScrollReveal>

        <div className="mt-12">
          <Link
            to="/features"
            className="inline-flex items-center gap-2 px-6 py-3 font-semibold text-xs tracking-wider text-white bg-gradient-to-r from-brand-orange to-brand-amber rounded-lg shadow-md shadow-brand-orange/15 hover:shadow-brand-amber/25 hover:scale-[1.02] transition-all"
          >
            <span>VIEW ALL FEATURES</span>
            <ChevronRight className="w-4 h-4" />
          </Link>
        </div>
      </section>

      {/* KEY BENEFITS SECTION */}
      <section
        ref={statsSectionRef}
        className="py-20 bg-bg-secondary/15 border-y border-text-muted/5 relative z-10"
      >
        <div className="max-w-7xl mx-auto px-6">
          <ScrollReveal staggerChildren={0.05} className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-6 text-center">

            <ScrollRevealItem>
              <div className="glass-panel p-6 rounded-xl border border-brand-orange/10">
                <div className="text-4xl font-title font-bold text-brand-amber number-glow">{stats.onboarding}%</div>
                <div className="text-[10px] font-bold text-text-secondary uppercase tracking-widest mt-2">
                  Faster Onboarding
                </div>
              </div>
            </ScrollRevealItem>

            <ScrollRevealItem>
              <div className="glass-panel p-6 rounded-xl border border-brand-orange/10">
                <div className="text-4xl font-title font-bold text-brand-orange number-glow">{stats.processing}%</div>
                <div className="text-[10px] font-bold text-text-secondary uppercase tracking-widest mt-2">
                  Manual Reduction
                </div>
              </div>
            </ScrollRevealItem>

            <ScrollRevealItem>
              <div className="glass-panel p-6 rounded-xl border border-brand-orange/10">
                <div className="text-4xl font-title font-bold text-brand-gold number-glow">{stats.accuracy}%</div>
                <div className="text-[10px] font-bold text-text-secondary uppercase tracking-widest mt-2">
                  Invoice Accuracy
                </div>
              </div>
            </ScrollRevealItem>

            <ScrollRevealItem>
              <div className="glass-panel p-6 rounded-xl border border-brand-orange/10">
                <div className="text-4xl font-title font-bold text-brand-amber number-glow">{stats.visibility}%</div>
                <div className="text-[10px] font-bold text-text-secondary uppercase tracking-widest mt-2">
                  Real-Time Visibility
                </div>
              </div>
            </ScrollRevealItem>

            <ScrollRevealItem>
              <div className="glass-panel p-6 rounded-xl border border-brand-orange/10">
                <div className="text-4xl font-title font-bold text-brand-orange number-glow">{stats.reduction}%</div>
                <div className="text-[10px] font-bold text-text-secondary uppercase tracking-widest mt-2">
                  Inventory Drop
                </div>
              </div>
            </ScrollRevealItem>

            <ScrollRevealItem>
              <div className="glass-panel p-6 rounded-xl border border-brand-orange/10 flex flex-col justify-center items-center h-full">
                <div className="text-4xl font-title font-bold text-neon-green number-glow">24/7</div>
                <div className="text-[10px] font-bold text-text-secondary uppercase tracking-widest mt-2">
                  AI Agent Monitoring
                </div>
              </div>
            </ScrollRevealItem>

          </ScrollReveal>
        </div>
      </section>

      {/* FEATURE PREVIEW SECTION */}
      <section className="py-24 max-w-7xl mx-auto px-6 relative z-10 text-center">
        <ScrollReveal className="max-w-3xl mx-auto mb-16 text-center">
          <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full border border-neon-purple/20 bg-brand-gold/5 text-brand-gold text-xs font-title tracking-wider mb-6">
            CORE PLATFORM MODULES
          </div>
          <h2 className="text-3xl sm:text-4xl font-title font-bold mb-6 tracking-wide">
            Enterprise Module Preview
          </h2>
          <p className="text-text-secondary text-base">
            Hover over each module capability to reveal operational focus and specs.
          </p>
        </ScrollReveal>

        <ScrollReveal staggerChildren={0.08} className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8 text-left">
          {modules.map((module, index) => (
            <ScrollRevealItem key={index} className="flex">
              <div
                className={`glass-panel rounded-2xl border border-brand-orange/10 ${module.hoverBorder} hover:scale-[1.02] transition-all duration-300 group overflow-hidden flex flex-col w-full`}
              >
                <div className="w-full h-44 overflow-hidden relative">
                  <div className="absolute inset-0 bg-gradient-to-t from-bg-primary via-transparent to-transparent z-10 opacity-70" />
                  <img
                    src={module.image}
                    alt={module.title}
                    className="w-full h-full object-cover transform scale-100 group-hover:scale-105 transition-all duration-700 ease-out filter brightness-[0.8] group-hover:brightness-100"
                  />
                </div>
                <div className="p-8 flex flex-col gap-3 flex-1">
                  <h3 className={`font-title text-base font-bold text-text-primary ${module.hoverColor} transition-colors`}>
                    {module.title}
                  </h3>
                  <p className="text-text-secondary text-xs leading-relaxed flex-1">
                    {module.desc}
                  </p>
                  <Link
                    to="/features"
                    className={`inline-flex items-center gap-1.5 font-title text-[9px] font-bold uppercase tracking-wider ${module.colorClass} mt-2`}
                  >
                    <span>Learn More</span>
                    <ChevronRight className="w-3.5 h-3.5" />
                  </Link>
                </div>
              </div>
            </ScrollRevealItem>
          ))}
        </ScrollReveal>

        <div className="mt-12">
          <Link
            to="/features"
            className="inline-flex items-center gap-2 text-xs font-title font-semibold tracking-wider text-brand-amber hover:text-text-primary hover:drop-shadow-[0_0_8px_rgba(0,180,216,0.5)] transition-all"
          >
            <span>EXPLORE ALL MODULES</span>
            <ArrowRight className="w-3.5 h-3.5" />
          </Link>
        </div>
      </section>

      {/* SMART DELIVERY SHOWCASE */}
      <section id="delivery-showcase" className="py-24 border-t border-text-muted/5 relative z-10 bg-bg-secondary/10">
        <div className="max-w-7xl mx-auto px-6 grid grid-cols-1 lg:grid-cols-12 gap-12 items-center">

          <ScrollReveal className="lg:col-span-7 text-left flex flex-col gap-6">
            <div className="inline-flex items-center gap-2 self-start px-3 py-1.5 rounded-full border border-neon-cyan/20 bg-brand-amber/5 text-brand-amber text-xs font-title tracking-wider">
              <span>DYNAMIC TRANSIT TELEMETRY</span>
            </div>
            <h2 className="text-3xl sm:text-4xl font-title font-bold tracking-wide">
              Smart Delivery & JIT Execution
            </h2>
            <p className="text-text-secondary text-sm leading-relaxed max-w-xl">
              Optimize incoming logistics with active coordinates tracking. Dynamically schedule warehouse docks, verify cargo using secure RFID tags, and maintain just-in-time warehouse operations.
            </p>
            <ul className="grid grid-cols-1 sm:grid-cols-2 gap-3.5 mt-2">
              <li className="flex items-center gap-2.5 text-xs text-text-secondary">
                <div className="w-1.5 h-1.5 rounded-full bg-neon-cyan shadow-[0_0_8px_rgba(0,180,216,0.8)]" />
                <span>Dynamic Delivery Slots</span>
              </li>
              <li className="flex items-center gap-2.5 text-xs text-text-secondary">
                <div className="w-1.5 h-1.5 rounded-full bg-neon-cyan shadow-[0_0_8px_rgba(0,180,216,0.8)]" />
                <span>GPS Vehicle Tracking</span>
              </li>
              <li className="flex items-center gap-2.5 text-xs text-text-secondary">
                <div className="w-1.5 h-1.5 rounded-full bg-neon-cyan shadow-[0_0_8px_rgba(0,180,216,0.8)]" />
                <span>Dock Scheduling</span>
              </li>
              <li className="flex items-center gap-2.5 text-xs text-text-secondary">
                <div className="w-1.5 h-1.5 rounded-full bg-neon-cyan shadow-[0_0_8px_rgba(0,180,216,0.8)]" />
                <span>QR/RFID Check-In Gates</span>
              </li>
            </ul>
            <div className="mt-4">
              <button
                onClick={() => alert("Simulating JIT Delivery route: coordinates matches active dock schedules.")}
                className="px-5 py-3 font-semibold text-xs tracking-wider text-text-primary border border-neon-cyan/35 hover:bg-brand-amber/5 rounded-lg transition-all"
              >
                SEE SMART DELIVERY IN ACTION
              </button>
            </div>
          </ScrollReveal>

          <ScrollReveal delay={0.15} className="lg:col-span-5 w-full h-[350px] sm:h-[400px] bg-bg-secondary/45 border border-brand-orange/15 rounded-2xl relative overflow-hidden">
            <div className="absolute top-4 left-4 z-10 flex items-center gap-2 px-2.5 py-1 bg-bg-primary/80 border border-text-muted/10 rounded-full">
              <span className="w-2 h-2 rounded-full bg-neon-cyan animate-ping" />
              <span className="text-[9px] font-title font-bold text-text-primary tracking-widest uppercase">
                YARD SENSORS ACTIVE
              </span>
            </div>
            <SmartDelivery3D />
          </ScrollReveal>

        </div>
      </section>

      {/* TEXT TRANSACTION TERMINAL */}
      <section id="transaction-terminal" className="py-24 border-t border-text-muted/5 relative z-10 bg-bg-secondary/5">
        <div className="max-w-7xl mx-auto px-6 grid grid-cols-1 lg:grid-cols-12 gap-12 items-center">

          {/* Left panel: Info & Input */}
          <ScrollReveal className="lg:col-span-6 text-left flex flex-col gap-6">
            <div className="inline-flex items-center gap-2 self-start px-3 py-1.5 rounded-full border border-brand-orange/20 bg-brand-orange/5 text-brand-orange text-xs font-title tracking-wider">
              <span>LEDGER SIMULATOR CORE</span>
            </div>
            <h2 className="text-3xl sm:text-4xl font-title font-bold tracking-wide">
              Autonomous Transaction Terminal
            </h2>
            <p className="text-text-secondary text-sm leading-relaxed max-w-xl">
              Type or select a text-based transaction directive. The platform will automatically parse the statement, identify the contractor, audit compliance rules, and commit details directly to the Paytm ledger.
            </p>

            {/* Quick Presets */}
            <div className="flex flex-col gap-2">
              <span className="text-[10px] font-bold text-text-muted uppercase tracking-wider">
                Select Preset Directives
              </span>
              <div className="flex flex-wrap gap-2">
                <button
                  onClick={() => setTransactionText("PO-8042: Ship 150 engines from McLaren for $180,000, JIT dock schedule 4")}
                  className="px-3 py-2 bg-bg-secondary/80 border border-text-muted/15 hover:border-brand-orange rounded-lg text-xs text-text-secondary hover:text-text-primary transition-all text-left"
                >
                  McLaren Engine Supply
                </button>
                <button
                  onClick={() => setTransactionText("INV-9102: Pay BRIGADE $45,000 for site materials, tax verified")}
                  className="px-3 py-2 bg-bg-secondary/80 border border-text-muted/15 hover:border-brand-orange rounded-lg text-xs text-text-secondary hover:text-text-primary transition-all text-left"
                >
                  BRIGADE Construction materials
                </button>
                <button
                  onClick={() => setTransactionText("REQ-3021: Order 500 cases from KALS GROUP, delivery Asian hub")}
                  className="px-3 py-2 bg-bg-secondary/80 border border-text-muted/15 hover:border-brand-orange rounded-lg text-xs text-text-secondary hover:text-text-primary transition-all text-left"
                >
                  KALS GROUP Order Request
                </button>
              </div>
            </div>

            {/* Interactive Input Form */}
            <div className="flex flex-col gap-3">
              <textarea
                value={transactionText}
                onChange={(e) => setTransactionText(e.target.value)}
                placeholder="Type your transaction directive here (e.g., Pay McLaren $180,000)..."
                rows={3}
                className="w-full px-4 py-3 bg-bg-secondary/80 border border-text-muted/15 rounded-xl text-xs text-text-primary focus:outline-none focus:border-brand-orange transition-colors"
              />
              <button
                onClick={() => handleProcessTransaction(transactionText)}
                disabled={isProcessingTx || !transactionText.trim()}
                className={`w-full py-3.5 rounded-lg font-semibold text-xs tracking-wider text-white bg-gradient-to-r from-brand-orange to-brand-amber shadow-md hover:scale-[1.01] active:scale-[0.99] transition-all flex items-center justify-center gap-2 ${isProcessingTx || !transactionText.trim() ? "opacity-50 cursor-not-allowed" : ""
                  }`}
              >
                {isProcessingTx ? (
                  <>
                    <span className="w-3.5 h-3.5 border-2 border-white border-t-transparent rounded-full animate-spin" />
                    <span>AUDITING TRANSACTION DIRECTIVE...</span>
                  </>
                ) : (
                  <span>PROCESS Autonomous TRANSACTION</span>
                )}
              </button>
            </div>
          </ScrollReveal>

          {/* Right panel: Terminal Console Output */}
          <ScrollReveal delay={0.15} className="lg:col-span-6 w-full flex flex-col gap-6">
            <div className="glass-panel rounded-2xl border border-brand-orange/15 overflow-hidden flex flex-col h-[400px] w-full text-left">
              {/* Terminal Title Bar */}
              <div className="px-4 py-3 bg-bg-secondary/80 border-b border-text-muted/10 flex justify-between items-center">
                <div className="flex items-center gap-2">
                  <span className="w-2.5 h-2.5 rounded-full bg-neon-red" />
                  <span className="w-2.5 h-2.5 rounded-full bg-brand-amber" />
                  <span className="w-2.5 h-2.5 rounded-full bg-neon-green" />
                  <span className="text-[10px] font-title font-bold text-text-muted tracking-wider uppercase ml-2">
                    Secured Transaction Console
                  </span>
                </div>
                {isProcessingTx && (
                  <span className="text-[9px] font-title font-bold text-brand-orange animate-pulse">
                    PROCESSING
                  </span>
                )}
              </div>

              {/* Terminal Logs */}
              <div className="p-5 flex-1 bg-black/90 font-mono text-[11px] text-brand-gold/90 overflow-y-auto flex flex-col gap-2.5 scrollbar-thin scrollbar-thumb-brand-orange">
                {transactionSteps.length === 0 && !txResult && (
                  <div className="text-text-muted/40 italic text-center my-auto">
                    Console idle. Enter or choose a directive on the left to initialize audit pipeline.
                  </div>
                )}
                {transactionSteps.map((step, idx) => (
                  <div key={idx} className="flex gap-2">
                    <span className="text-brand-orange font-bold">&gt;&gt;</span>
                    <span>{step}</span>
                  </div>
                ))}

                {/* Simulated Ledger Receipt */}
                {txResult && (
                  <div className="mt-4 p-4 border border-brand-orange/20 bg-bg-primary/90 rounded-lg flex flex-col gap-3 text-text-primary animate-pulse">
                    <div className="flex justify-between items-center border-b border-text-muted/10 pb-2">
                      <span className="text-xs font-bold font-title tracking-wider text-transparent bg-clip-text bg-gradient-to-r from-brand-orange to-brand-amber">
                        PAYTM AP LEDGER RECEIPT
                      </span>
                      <span className="px-2 py-0.5 rounded text-[9px] font-bold bg-brand-orange/20 text-brand-orange uppercase border border-brand-orange/30">
                        {txResult.status}
                      </span>
                    </div>

                    <div className="grid grid-cols-2 gap-2 text-[10px]">
                      <div>
                        <span className="text-text-muted uppercase block">Recipient Vendor</span>
                        <span className="font-bold text-text-primary">{txResult.vendor}</span>
                      </div>
                      <div>
                        <span className="text-text-muted uppercase block">Authorized Value</span>
                        <span className="font-bold text-text-primary">{txResult.amount}</span>
                      </div>
                      <div className="col-span-2">
                        <span className="text-text-muted uppercase block">Audit Blockchain Hash</span>
                        <span className="font-mono text-brand-amber font-bold break-all">{txResult.hash}</span>
                      </div>
                      <div>
                        <span className="text-text-muted uppercase block">Timestamp</span>
                        <span className="font-bold text-text-primary">{txResult.timestamp}</span>
                      </div>
                      <div>
                        <span className="text-text-muted uppercase block">Assurance Level</span>
                        <span className="font-bold text-neon-green">99.9% SECURE</span>
                      </div>
                    </div>
                  </div>
                )}
              </div>
            </div>
          </ScrollReveal>

        </div>
      </section>

      {/* INTEGRATIONS SECTION */}
      <section className="py-24 border-t border-text-muted/5 relative z-10 bg-bg-secondary/15 overflow-hidden">


        <div className="max-w-7xl mx-auto px-6 text-center">
          {/* Mobile and Tablet Layout (Grid View) */}
          <div className="lg:hidden">
            <ScrollReveal className="text-center mb-12">
              <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full border border-brand-orange/20 bg-brand-orange/5 text-brand-orange text-xs font-title tracking-wider mb-6">
                CONNECTED SYSTEMS
              </div>
              <h2 className="text-3xl sm:text-4xl font-title font-bold mb-6 tracking-wide">
                Works With Your Existing ERP Systems
              </h2>
              <p className="text-text-secondary text-base max-w-2xl mx-auto">
                Synchronize supplier logs, transaction sheets, and ledger balances dynamically.
              </p>
            </ScrollReveal>

            <ScrollReveal staggerChildren={0.05} className="grid grid-cols-2 sm:grid-cols-3 gap-4 max-w-3xl mx-auto">
              {integrations.map((item, index) => (
                <ScrollRevealItem key={index} className="flex">
                  <div
                    className="glass-panel p-5 rounded-xl border border-brand-orange/10 flex flex-col items-center justify-center gap-3 group hover:scale-[1.03] transition-all duration-300 hover:border-brand-amber/30 cursor-pointer w-full"
                    title={item.name}
                  >
                    <div className={`w-12 h-12 rounded-full bg-bg-secondary/40 border border-text-muted/10 flex items-center justify-center transition-all duration-300 group-hover:bg-bg-primary/60 group-hover:border-text-muted/20 ${item.defaultColor || 'text-text-muted/50'} ${item.defaultGlow || ''} ${item.hoverColor} ${item.hoverGlow}`}>
                      {item.svg}
                    </div>
                    <div className="flex flex-col items-center justify-center text-center">
                      <div className={`${item.textColor} font-bold tracking-wider font-title text-xs transition-colors duration-300`}>
                        {item.name}
                      </div>
                      <span className="text-[8px] text-text-secondary font-medium tracking-wide uppercase opacity-75">
                        {item.spec}
                      </span>
                    </div>
                  </div>
                </ScrollRevealItem>
              ))}
            </ScrollReveal>
          </div>

          {/* Desktop Layout (Circular Arch/Fan Dome Layout) */}
          <div
            className="hidden lg:block relative w-full max-w-5xl mx-auto h-[540px] select-none"
            onMouseEnter={() => setIsPaused(true)}
            onMouseLeave={() => setIsPaused(false)}
          >
            
            {/* Center Radial Glow Aura */}
            <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[650px] h-[450px] bg-[radial-gradient(circle_at_center,rgba(0,242,254,0.14)_0%,rgba(249,115,22,0.04)_45%,transparent_75%)] pointer-events-none z-0 blur-2xl" />

            {/* Central Info Card & Content */}
            <div className="absolute left-1/2 bottom-4 -translate-x-1/2 w-full max-w-2xl text-center z-10 flex flex-col items-center gap-5">
              <div className="inline-flex items-center px-4 py-1.5 bg-bg-secondary/60 border border-text-muted/15 rounded-full text-[10px] font-bold text-text-secondary tracking-widest uppercase shadow-sm">
                Integrations
              </div>
              
              <h3 className="text-4xl lg:text-5xl font-title font-black text-text-primary tracking-wide leading-tight drop-shadow-[0_0_12px_rgba(255,255,255,0.15)]">
                Connect your tools
              </h3>
              
              <p className="text-text-secondary text-sm leading-relaxed max-w-lg">
                Supply Spot brings suppliers, contracts, compliance, and accounts payable together by syncing your existing enterprise tools into one unified workspace.
              </p>
              
              <div className="flex items-center gap-8 mt-2 text-[10px] font-bold text-text-muted tracking-wider uppercase border-t border-text-muted/10 pt-4 w-full justify-center">
                <span className="flex items-center gap-2">
                  <ShieldCheck className="w-4 h-4 text-brand-orange" />
                  <span>Secure connections</span>
                </span>
                <span className="flex items-center gap-2">
                  <Eye className="w-4 h-4 text-brand-amber" />
                  <span>Read only</span>
                </span>
                <span className="flex items-center gap-2">
                  <Power className="w-4 h-4 text-brand-gold" />
                  <span>Disconnect anytime</span>
                </span>
              </div>
            </div>

            {/* Circular Arch Cards */}
            {(() => {
              const t = rotationPhase / 30;
              const startS = Math.floor(-t) - 1;
              const visibleSlots = [];
              
              for (let k = 0; k <= 8; k++) {
                const s = startS + k;
                const angle = 180 - (s + t) * 30;
                if (angle >= -35 && angle <= 215) {
                  visibleSlots.push({ s, angle });
                }
              }

              return visibleSlots.map(({ s, angle }) => {
                const itemIndex = ((s % 7) + 7) % 7;
                const item = integrations[itemIndex];
                const rad = (angle * Math.PI) / 180;
                
                // Ellipse parameters matching original layout
                const left = 50 + 40 * Math.cos(rad);
                const top = 72 - 56 * Math.sin(rad);
                
                // Opacity fades out at ends
                let opacity = 1;
                if (angle > 180) {
                  opacity = Math.max(0, 1 - (angle - 180) / 30);
                } else if (angle < 0) {
                  opacity = Math.max(0, 1 - (0 - angle) / 30);
                }
                
                const iconRotate = `${(angle - 90) * 0.388}deg`;

                return (
                  <div
                    key={`${s}-${itemIndex}`}
                    className="absolute z-20 pointer-events-auto"
                    style={{
                      left: `${left}%`,
                      top: `${top}%`,
                      transform: "translate(-50%, -50%)",
                      opacity: opacity,
                      transition: "opacity 0.1s linear",
                    }}
                  >
                    <div
                      className="glass-panel p-4.5 rounded-2xl border border-brand-orange/10 flex items-center justify-center group hover:scale-110 hover:-translate-y-1 transition-all duration-300 hover:border-brand-amber/40 cursor-pointer shadow-lg w-18 h-18 bg-bg-secondary/40 backdrop-blur-md hover:shadow-brand-orange/10"
                      title={`${item.name} (${item.spec})`}
                    >
                      <div className={`w-10 h-10 rounded-full flex items-center justify-center transition-all duration-300 group-hover:scale-105 ${item.defaultColor || 'text-text-muted/65'} ${item.defaultGlow || ''} ${item.hoverColor} ${item.hoverGlow}`}>
                        <div
                          style={{ transform: `rotate(${iconRotate})` }}
                          className="flex items-center justify-center w-full h-full"
                        >
                          {item.svg}
                        </div>
                      </div>
                    </div>
                  </div>
                );
              });
            })()}

          </div>
        </div>
      </section>

      {/* TESTIMONIALS */}
      <section className="py-24 max-w-7xl mx-auto px-6 relative z-10 text-center">
        <ScrollReveal className="max-w-3xl mx-auto mb-16 text-center">
          <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full border border-neon-cyan/20 bg-brand-amber/5 text-brand-amber text-xs font-title tracking-wider mb-6">
            CUSTOMER SUCCESS
          </div>
          <h2 className="text-3xl sm:text-4xl font-title font-bold mb-6 tracking-wide">
            Operational Results
          </h2>
        </ScrollReveal>

        <ScrollReveal staggerChildren={0.1} className="grid grid-cols-1 md:grid-cols-3 gap-8 text-left">

          <ScrollRevealItem className="flex">
            <div className="glass-panel p-8 rounded-2xl border border-brand-orange/10 flex flex-col justify-between gap-6 w-full">
              <p className="text-text-secondary text-sm leading-relaxed italic">
                "Supply Spot decreased our supplier registration times from weeks to under 48 hours. The Live 3D telemetry routes help us re-route logistics blocks immediately."
              </p>
              <div>
                <div className="text-2xl font-title font-bold text-brand-amber">40% Faster</div>
                <div className="text-[10px] font-bold text-text-muted uppercase tracking-widest mb-3">Onboarding Speed</div>
                <div className="text-xs font-bold text-text-primary">Marcus Vance</div>
                <div className="text-[10px] text-text-secondary">VP of Logistics, Global Tech Corp</div>
              </div>
            </div>
          </ScrollRevealItem>

          <ScrollRevealItem className="flex">
            <div className="glass-panel p-8 rounded-2xl border border-brand-orange/10 flex flex-col justify-between gap-6 w-full">
              <p className="text-text-secondary text-sm leading-relaxed italic">
                "Our auditing crew saves hundreds of hours monthly. The 3-way invoice auto-match matches PO records against payment escrows with absolute verification."
              </p>
              <div>
                <div className="text-2xl font-title font-bold text-brand-orange">90% Auto</div>
                <div className="text-[10px] font-bold text-text-muted uppercase tracking-widest mb-3">Invoice Processing</div>
                <div className="text-xs font-bold text-text-primary">Sarah Kincaid</div>
                <div className="text-[10px] text-text-secondary">Head of Procurement, Orion Automotive</div>
              </div>
            </div>
          </ScrollRevealItem>

          <ScrollRevealItem className="flex">
            <div className="glass-panel p-8 rounded-2xl border border-brand-orange/10 flex flex-col justify-between gap-6 w-full">
              <p className="text-text-secondary text-sm leading-relaxed italic">
                "Connecting warehouses with dynamic shipping gates has streamlined our logistics pipelines. Our JIT warehouse docks report almost zero wait cycles."
              </p>
              <div>
                <div className="text-2xl font-title font-bold text-brand-gold">30% Better</div>
                <div className="text-[10px] font-bold text-text-muted uppercase tracking-widest mb-3">On-Time Deliveries</div>
                <div className="text-xs font-bold text-text-primary">Tyler Loebeck</div>
                <div className="text-[10px] text-text-secondary">Director of Operations, Apex Industries</div>
              </div>
            </div>
          </ScrollRevealItem>

        </ScrollReveal>
      </section>

      {/* FINAL CTA SECTION */}
      <section id="contact" className="py-24 relative overflow-hidden z-10 border-t border-text-muted/5">
        <div className="absolute inset-0 z-0 opacity-15 pointer-events-none">
          <HeroGlobe />
        </div>
        <ScrollReveal className="max-w-4xl mx-auto px-6 relative z-10 text-center flex flex-col gap-6 items-center">
          <h2 className="text-4xl sm:text-5xl font-title font-bold tracking-wide leading-none">
            Ready to Transform Your Supply Chain?
          </h2>
          <p className="text-text-secondary text-base md:text-lg leading-relaxed max-w-xl">
            Connect suppliers, automate procurement, optimize JIT logistics, and accelerate invoice matching from one intelligent command core.
          </p>
          <div className="flex flex-wrap justify-center items-center gap-4 mt-2">
            <button
              onClick={() => alert("Connecting secure reservation line... DEMO REQUEST DISPATCHED.")}
              className="px-8 py-4 font-semibold text-xs tracking-wider text-white bg-gradient-to-r from-brand-orange to-brand-amber rounded-lg shadow-lg shadow-brand-orange/20 hover:scale-[1.02] active:scale-[0.98] transition-all"
            >
              BOOK SECURE DEMO
            </button>
            <Link
              to="https://supplyspot.ai/"
              className="px-8 py-4 font-semibold text-xs tracking-wider text-text-primary border border-text-muted/30 hover:border-brand-orange rounded-lg bg-transparent hover:text-brand-amber transition-all"
            >
              START FREE TRIAL
            </Link>
          </div>
        </ScrollReveal>
      </section>

      {/* FOOTER */}
      <Footer />
    </div>
  );
}
